import { memo, useEffect, useState, useCallback } from 'react';
import { FileText, Loader2, Star, Plus, ChevronRight, Sparkles, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { useWorkflowStore } from '../../store/workflowStore';
import { useRevealStore } from '../../store/revealStore';
import * as projectService from '../../services/projectService';
import { PREDEFINED_TEMPLATES } from '../../data/predefinedTemplates';

/**
 * Empty-canvas welcome panel.
 * Shows a grid of templates (predefined + user's own) — click to instantiate
 * as a new project and open it.
 */
const EmptyCanvasTemplates = memo(() => {
  const [userTemplates, setUserTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [instantiatingId, setInstantiatingId] = useState(null);

  const loadProject = useWorkflowStore(s => s.loadProject);
  const loadSavedProjects = useWorkflowStore(s => s.loadSavedProjects);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await projectService.listTemplates();
      setUserTemplates(res?.templates || []);
    } catch (err) {
      // If migration 005 isn't applied yet, the endpoint 500s. Keep predefined-only.
      console.warn('[EmptyCanvas] listTemplates failed — showing predefined only.', err?.message);
      setUserTemplates([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleUse = async (tpl) => {
    if (instantiatingId) return;
    setInstantiatingId(tpl.id);
    try {
      const payload = tpl.predefined
        ? {
            name: tpl.name,
            nodes: tpl.nodes,
            edges: tpl.edges,
            node_id_counter: tpl.node_id_counter,
          }
        : { template_id: tpl.id };

      const res = await projectService.instantiateTemplate(payload);
      const newProject = res?.project;
      if (!newProject?.id) throw new Error('Bad response');

      await loadSavedProjects();
      const ok = await loadProject(newProject.id);
      if (ok) {
        // El proyecto ya está creado y persistido: esto sólo anima su
        // aparición en el canvas (capa visual, ver store/revealStore.js).
        try {
          const st = useWorkflowStore.getState();
          useRevealStore.getState().start({
            nodeIds: st.nodes.map((n) => n.id),
            edgeIds: st.edges.map((e) => e.id),
          });
        } catch (revealErr) {
          console.warn('[EmptyCanvas] no se pudo animar la aparición:', revealErr);
        }
        toast.success(`"${newProject.name}" creado desde plantilla`, { duration: 2000, position: 'bottom-right' });
      }
    } catch (err) {
      console.error('[EmptyCanvas] instantiate failed:', err);
      toast.error('No se pudo crear el proyecto desde la plantilla', { position: 'bottom-right' });
    } finally {
      setInstantiatingId(null);
    }
  };

  const allTemplates = [...PREDEFINED_TEMPLATES, ...userTemplates];

  return (
    <div className="canvas-empty-state" data-testid="canvas-empty-state">
      <div className="empty-state-content empty-state-wide">
        <div className="empty-state-head">
          <h3>Comienza tu workflow</h3>
          <p>Elige una plantilla o arrastra herramientas desde el panel izquierdo</p>
        </div>

        <button
          type="button"
          className="agent-hero-card"
          onClick={() => window.dispatchEvent(new Event('lumiere:open'))}
          data-testid="empty-canvas-agent-card"
        >
          <div className="agent-hero-icon"><Sparkles size={22} /></div>
          <div className="agent-hero-body">
            <div className="agent-hero-title">
              <Wand2 size={14} /> Lumière · Agente Creativo
              <span className="agent-hero-badge">NUEVO</span>
            </div>
            <div className="agent-hero-desc">
              Describí lo que querés lograr o pegá un guion — Lumière arma el flujo completo con los nodos correctos, prompts pre-cargados y encadenamiento inteligente entre escenas.
            </div>
          </div>
          <ChevronRight size={18} />
        </button>

        <div className="templates-grid" data-testid="templates-grid">
          {/* Predefined first */}
          {PREDEFINED_TEMPLATES.map((tpl) => {
            const Icon = tpl.icon;
            const loadingCurrent = instantiatingId === tpl.id;
            return (
              <button
                key={tpl.id}
                type="button"
                className="template-card template-predefined"
                onClick={() => handleUse(tpl)}
                disabled={!!instantiatingId}
                data-testid={`template-card-${tpl.id}`}
                style={{ '--template-accent': tpl.accent }}
              >
                <div className="template-card-icon">
                  {loadingCurrent ? <Loader2 size={20} className="spin-icon" /> : <Icon size={20} />}
                </div>
                <div className="template-card-body">
                  <div className="template-card-head">
                    <span className="template-card-name">{tpl.name}</span>
                    <span className="template-badge template-badge-predef">
                      <Star size={9} /> Predefinida
                    </span>
                  </div>
                  <span className="template-card-desc">{tpl.description}</span>
                  <span className="template-card-meta">
                    {tpl.nodes.length} nodos <ChevronRight size={12} />
                  </span>
                </div>
              </button>
            );
          })}

          {/* User templates */}
          {userTemplates.map((tpl) => {
            const loadingCurrent = instantiatingId === tpl.id;
            const nodeCount = Array.isArray(tpl.nodes) ? tpl.nodes.length : 0;
            return (
              <button
                key={tpl.id}
                type="button"
                className="template-card template-user"
                onClick={() => handleUse(tpl)}
                disabled={!!instantiatingId}
                data-testid={`template-card-${tpl.id}`}
              >
                <div className="template-card-icon">
                  {loadingCurrent ? <Loader2 size={20} className="spin-icon" /> : <FileText size={20} />}
                </div>
                <div className="template-card-body">
                  <div className="template-card-head">
                    <span className="template-card-name">{tpl.name}</span>
                    <span className="template-badge template-badge-user">
                      <Star size={9} /> Tuya
                    </span>
                  </div>
                  {tpl.description && (
                    <span className="template-card-desc">{tpl.description}</span>
                  )}
                  <span className="template-card-meta">
                    {nodeCount} nodos <ChevronRight size={12} />
                  </span>
                </div>
              </button>
            );
          })}

          {/* Empty hint when user has no templates yet */}
          {!loading && userTemplates.length === 0 && (
            <div className="template-empty-hint" data-testid="template-empty-hint">
              <div className="template-empty-hint-icon"><Plus size={16} /></div>
              <span>Crea un proyecto y guárdalo como plantilla para que aparezca acá.</span>
            </div>
          )}
        </div>

        <div className="empty-state-divider">
          <span>o crea desde cero</span>
        </div>

        <p className="connection-hint">
          Conecta nodos arrastrando desde el punto verde de salida hasta el punto de entrada
        </p>
      </div>
    </div>
  );
});

EmptyCanvasTemplates.displayName = 'EmptyCanvasTemplates';
export default EmptyCanvasTemplates;
